import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { SessionService } from 'src/app/services/session-service';
import { whatsOnYourPlateEnabled } from 'src/app/site-config';
import {
  guidePath,
  guideReaderPath,
  MarketGuidesService,
  pounds,
  Publication,
  PublicationPreviewPage,
  ukDay,
} from '../market-guides.service';

/**
 * /market-guides/{slug}: the guide's public landing page. Built to stand on
 * its own for a visitor arriving from Instagram: the real cover, what the
 * research found, sample pages and one clear way to claim, all before any
 * sign-in. Claiming opens the site's sign-in / register modal in place and
 * goes straight to the reader once the guide is in the account.
 */
@Component({
  selector: 'pla-guide-landing',
  templateUrl: './guide-landing.component.html',
  styleUrls: ['./guide-landing.component.scss'],
})
export class GuideLandingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('heroCta') heroCta: ElementRef<HTMLElement>;

  public slug = '';
  public pub: Publication | null = null;
  public state: 'loading' | 'ready' | 'missing' | 'error' = 'loading';
  public claiming = false;
  public claimError = '';
  public stickyCta = false;
  public lightbox: number | null = null;
  public readonly showWoyp = whatsOnYourPlateEnabled;

  private subs: Subscription[] = [];
  private observer: IntersectionObserver | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guides: MarketGuidesService,
    private session: SessionService,
    private quickSignup: QuickSignupService,
    private title: Title,
    private meta: Meta,
    private zone: NgZone,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    const query = this.route.snapshot.queryParams || {};
    this.guides.rememberContext(query, guidePath(this.slug));
    this.load(query['claim'] === '1');
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') return;
    this.observer = new IntersectionObserver(([entry]) =>
      this.zone.run(() => (this.stickyCta = !entry.isIntersecting && entry.boundingClientRect.top < 0))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.observer?.disconnect();
  }

  private load(autoClaim = false): void {
    this.subs.push(
      this.guides.get(this.slug).subscribe({
        next: (p) => {
          this.pub = p;
          this.state = 'ready';
          this.setHead(p);
          setTimeout(() => this.heroCta && this.observer?.observe(this.heroCta.nativeElement));
          // Back from a sign-in that left the page (Apple): finish the claim.
          if (autoClaim && this.session.isLoggedIn()) {
            this.location.replaceState(guidePath(this.slug));
            if (p.access.entitled) this.openReader();
            else if (p.phase === 'complimentary') this.claimNow();
          } else if (autoClaim && p.phase === 'complimentary') {
            // Ad link (?claim=1) for a signed-out visitor: open the free sign-up form straight away.
            // The page stays underneath, so closing the form leaves them on the guide.
            this.location.replaceState(guidePath(this.slug));
            setTimeout(() => this.primaryAction());
          }
        },
        error: (e) => (this.state = e?.status === 404 ? 'missing' : 'error'),
      })
    );
  }

  private setHead(p: Publication): void {
    this.title.setTitle(`${p.title}: ${p.subtitle} | PlateX Market Guides`);
    if (p.description) {
      this.meta.updateTag({ name: 'description', content: p.description });
      this.meta.updateTag({ property: 'og:description', content: p.description });
    }
    const img = p.landing?.shareImage || p.coverUrl;
    if (img) this.meta.updateTag({ property: 'og:image', content: img });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
  }

  // ------------------------------------------------------------ access

  get entitled(): boolean {
    return !!this.pub?.access.entitled;
  }

  /** Last full day of the complimentary window, UK time ("9 November"). */
  get freeUntil(): string {
    const end = this.pub?.complimentaryEndsAt;
    return end ? ukDay(new Date(Date.parse(end) - 60000).toISOString()) : '';
  }

  get opensOn(): string {
    return ukDay(this.pub?.launchAt || null);
  }

  get price(): string {
    return pounds(this.pub?.priceMinor ?? null);
  }

  get resumePage(): number {
    const p = this.pub?.access.lastPage || 0;
    return p > 1 && p < (this.pub?.pageCount || 0) ? p : 0;
  }

  primaryAction(): void {
    if (!this.pub) return;
    if (this.entitled) return this.openReader();
    if (this.pub.phase !== 'complimentary') return;
    if (this.session.isLoggedIn()) return this.claimNow();

    // Covers the sign-in route that leaves the site (Apple) and lands on /login.
    this.session.setSuccessRoute(`${guidePath(this.slug)}?claim=1`);
    this.quickSignup.quickSignup(
      `market-guide-${this.slug}`,
      undefined,
      (ok) => ok && this.claimNow(),
      () => {},
      undefined,
      'register',
      {
        registerTitle: 'Get your free guide',
        registerText: `Create a free PlateX account to read ${this.pub.title} straight away. It's yours to keep, and your account also unlocks the full 27 release research.`,
        loginText: `Sign in to add ${this.pub.title} to your account.`,
      }
    );
  }

  claimNow(): void {
    if (this.claiming) return;
    this.claiming = true;
    this.claimError = '';
    this.guides.claim(this.slug).subscribe({
      next: (p) => {
        this.pub = p;
        this.claiming = false;
        if (p.access.entitled) this.openReader();
      },
      error: (e) => {
        this.claiming = false;
        if (e?.status === 409) {
          this.claimError = 'The complimentary edition is no longer available to claim.';
          this.load();
        } else {
          this.claimError = 'Something went wrong claiming the guide. Please try again.';
        }
      },
    });
  }

  openReader(): void {
    this.router.navigateByUrl(guideReaderPath(this.slug));
  }

  // ------------------------------------------------------------ preview

  get previews(): PublicationPreviewPage[] {
    return this.pub?.previewPages || [];
  }

  openPreview(i: number): void {
    this.lightbox = i;
  }

  closePreview(): void {
    this.lightbox = null;
  }

  stepPreview(d: number): void {
    if (this.lightbox == null) return;
    const n = this.previews.length;
    this.lightbox = (this.lightbox + d + n) % n;
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (this.lightbox == null) return;
    if (e.key === 'Escape') this.closePreview();
    else if (e.key === 'ArrowRight') this.stepPreview(1);
    else if (e.key === 'ArrowLeft') this.stepPreview(-1);
  }

  private touchX: number | null = null;
  touchStart(e: TouchEvent): void {
    this.touchX = e.touches[0]?.clientX ?? null;
  }
  touchEnd(e: TouchEvent): void {
    if (this.touchX == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? this.touchX) - this.touchX;
    this.touchX = null;
    if (Math.abs(dx) > 40) this.stepPreview(dx < 0 ? 1 : -1);
  }

  scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
